import { calculateCheckedLpgPayback, calculateCheckedFuelConsumption, calculateCheckedProfitMargin, calculateCheckedBmr, calculateCheckedIdealWeight } from '../../source/shared/pilot-boundaries.js';
import { isResolvedPolicySnapshot } from '../../source/shared/period-policy.ts';
import './coreEndpoints.js';
import {
  calculateBmr,
  calculateFraction,
  calculateFuelConsumption,
  calculateGcdLcm,
  calculateGeometry,
  calculateHomeRenovation,
  calculateHgsToll,
  calculateIdealWeightReference,
  calculateLpgPayback,
  calculatePercentage,
  calculatePowerRoot,
  calculatePrime,
  calculateProfitMargin,
  calculatePythagorean,
  calculateQuadratic,
  calculateRatio,
  calculateSalary,
  type BmrConfig,
  type BmrInput,
  type FractionInput,
  type FractionOperation,
  type FinanceBusinessRuntimeMap,
  type FinanceBusinessToolId,
  type FuelConsumptionConfig,
  type FuelConsumptionInput,
  type GcdLcmInput,
  type GeometryInput,
  type GeometryShape,
  type HgsCrossing,
  type HgsDirection,
  type HgsTime,
  type HgsVehicleClass,
  type IdealWeightConfig,
  type IdealWeightInput,
  type LpgPaybackConfig,
  type LpgPaybackInput,
  type PercentageInput,
  type PowerRootInput,
  type PowerRootMode,
  type PrimeInput,
  type ProfitMarginConfig,
  type ProfitMarginInput,
  type PythagoreanInput,
  type PythagoreanMode,
  type QuadraticInput,
  type RatioInput,
  type RatioMode,
  type SalaryMode,
} from '@hephesapla/calculation-core';
import {
  assertToolEffectiveForDate,
  assertHgsCrossingEffective,
  calculateDateTimeTool,
  calculateEducationTool,
  calculateFinanceBusinessTool,
  fractionConfigFor,
  gcdLcmConfigFor,
  geometryConfigFor,
  homeRenovationConfigFor,
  homeRenovationInputFor,
  hgsConfigFor,
  percentageConfigFor,
  powerRootConfigFor,
  primeNumberConfigFor,
  pythagoreanConfigFor,
  quadraticEquationConfigFor,
  ratioProportionConfigFor,
  salaryConfigFor,
} from '@hephesapla/runtime-adapter';
import type {
  EverydayHealthToolId,
  ToolDefinition,
} from '@hephesapla/tool-registry';

export type LegacySalaryMode = 'brutNet' | 'netBrut';
export type LegacyPercentageMode = 'yuzdesi' | 'hangiYuzde' | 'artis' | 'degisim';
export type LegacyHgsCrossing =
  | '15temmuz'
  | 'fsm'
  | 'yss'
  | 'osmangazi'
  | 'canakkale'
  | 'avrasya';
export type LegacyGeometryShape =
  | 'kare' | 'dikdortgen' | 'ucgen' | 'daire' | 'yamuk'
  | 'paralelkenar' | 'kup' | 'kure' | 'silindir' | 'koni';
export type LegacyPowerRootMode = 'us' | 'karekok' | 'ndereceKok' | 'log' | 'faktoriyel' | 'mutlak';
export type LegacyFractionOperation = 'topla' | 'cikar' | 'carp' | 'bol';
export type LegacyRatioMode = 'duz' | 'ters';
export type LegacyPythagoreanMode = 'hipotenus' | 'dikKenar';

const salaryModeMap: Readonly<Record<LegacySalaryMode, SalaryMode>> = {
  brutNet: 'gross-to-net',
  netBrut: 'net-to-gross',
};

const percentageModeMap: Readonly<Record<LegacyPercentageMode, PercentageInput['mode']>> = {
  yuzdesi: 'percentage-of',
  hangiYuzde: 'what-percentage',
  artis: 'increase-decrease',
  degisim: 'percentage-change',
};

const crossingMap: Readonly<Record<LegacyHgsCrossing, HgsCrossing>> = {
  '15temmuz': '15-temmuz',
  fsm: 'fsm',
  yss: 'yss',
  osmangazi: 'osmangazi',
  canakkale: '1915-canakkale',
  avrasya: 'avrasya',
};

const geometryShapeMap: Readonly<Record<LegacyGeometryShape, GeometryShape>> = {
  kare: 'square', dikdortgen: 'rectangle', ucgen: 'triangle', daire: 'circle',
  yamuk: 'trapezoid', paralelkenar: 'parallelogram', kup: 'cube', kure: 'sphere',
  silindir: 'cylinder', koni: 'cone',
};

const powerRootModeMap: Readonly<Record<LegacyPowerRootMode, PowerRootMode>> = {
  us: 'power', karekok: 'sqrt', ndereceKok: 'nth-root', log: 'logarithm',
  faktoriyel: 'factorial', mutlak: 'absolute',
};

const fractionOperationMap: Readonly<Record<LegacyFractionOperation, FractionOperation>> = {
  topla: 'add', cikar: 'subtract', carp: 'multiply', bol: 'divide',
};

const ratioModeMap: Readonly<Record<LegacyRatioMode, RatioMode>> = {
  duz: 'direct', ters: 'inverse',
};

const pythagoreanModeMap: Readonly<Record<LegacyPythagoreanMode, PythagoreanMode>> = {
  hipotenus: 'hypotenuse', dikKenar: 'leg',
};

function mappedValue<Legacy extends string, Canonical>(
  map: Readonly<Record<Legacy, Canonical>>,
  value: Legacy,
  context: string,
): Canonical {
  // DOM değeri TypeScript sınırını atlayabildiği için prototype zinciri değil,
  // yalnız kapalı sözleşmenin kendi anahtarları kabul edilir.
  if (!Object.prototype.hasOwnProperty.call(map, value)) {
    throw new Error(`${context} desteklenmiyor: ${String(value)}.`);
  }
  return map[value];
}

/** Mevcut HTML radio değerini ortak çekirdeğin kanonik moduna çevirir. */
export function canonicalSalaryMode(value: LegacySalaryMode): SalaryMode {
  return mappedValue(salaryModeMap, value, 'Maaş modu');
}

/** Mevcut yüzde sayfasındaki dört radio değerini ortak çekirdeğe eşler. */
export function canonicalPercentageMode(
  value: LegacyPercentageMode,
): PercentageInput['mode'] {
  return mappedValue(percentageModeMap, value, 'Yüzde modu');
}

/** Mevcut HGS select değerlerindeki iki tarihsel kısaltmayı açıkça eşler. */
export function canonicalHgsCrossing(value: LegacyHgsCrossing): HgsCrossing {
  return mappedValue(crossingMap, value, 'HGS geçişi');
}

export function canonicalGeometryShape(value: LegacyGeometryShape): GeometryShape {
  return mappedValue(geometryShapeMap, value, 'Geometri şekli');
}

export function canonicalPowerRootMode(value: LegacyPowerRootMode): PowerRootMode {
  return mappedValue(powerRootModeMap, value, 'Üs/kök modu');
}

export function canonicalFractionOperation(value: LegacyFractionOperation): FractionOperation {
  return mappedValue(fractionOperationMap, value, 'Kesir işlemi');
}

export function canonicalRatioMode(value: LegacyRatioMode): RatioMode {
  return mappedValue(ratioModeMap, value, 'Orantı modu');
}

export function canonicalPythagoreanMode(value: LegacyPythagoreanMode): PythagoreanMode {
  return mappedValue(pythagoreanModeMap, value, 'Pisagor modu');
}

/** Gece alanını sabit geçiş adına değil, etkin registry tarifesine bağlar. */
export function legacyHgsHasNightTariff(
  tool: ToolDefinition,
  legacyCrossing: LegacyHgsCrossing,
  currentDate: string,
): boolean {
  const crossing = canonicalHgsCrossing(legacyCrossing);
  assertToolEffectiveForDate(tool, currentDate);
  assertHgsCrossingEffective(tool, crossing, currentDate);
  if (tool.config.kind !== 'hgs-fixed-tariffs-v1') {
    throw new Error('HGS aracı beklenmeyen konfigürasyon taşıyor.');
  }
  return tool.config.crossings.find((item) => item.id === crossing)
    ?.nightPricesByVehicleClass !== null;
}

export function calculateWebPercentage(
  tool: ToolDefinition,
  input: PercentageInput,
  currentDate: string,
) {
  assertToolEffectiveForDate(tool, currentDate);
  return calculatePercentage(input, percentageConfigFor(tool));
}

/**
 * Finans/işletme sayfalarında formül ya da dönemsel oran tutulmaz. Sayfanın
 * kapalı araç kimliği ile doğrulanmış registry kaydı birlikte runtime adaptera
 * verilir; tarih de test edilebilir biçimde çağıran tarafından geçirilir.
 */
export function calculateWebFinanceBusiness<TToolId extends FinanceBusinessToolId>(
  tool: ToolDefinition & { readonly toolId: TToolId },
  input: FinanceBusinessRuntimeMap[TToolId]['input'],
  currentDate: string,
  period?: import('../../source/shared/period-policy.ts').ResolvedPolicySnapshot,
) {
  return calculateFinanceBusinessTool(tool, input, { currentDate, period });
}

export function calculateLegacyWebPercentage(
  tool: ToolDefinition,
  input: { readonly mode: LegacyPercentageMode; readonly x: number; readonly y: number },
  currentDate: string,
) {
  return calculateWebPercentage(
    tool,
    { mode: canonicalPercentageMode(input.mode), x: input.x, y: input.y },
    currentDate,
  );
}

export function calculateLegacyWebSalary(
  tool: ToolDefinition,
  input: { readonly mode: LegacySalaryMode; readonly monthlyAmount: number },
  currentDate: string,
  period?: import('../../source/shared/period-policy.ts').ResolvedPolicySnapshot,
) {
  if (period) {
    if (!isResolvedPolicySnapshot(period) || period.toolId !== tool.toolId || period.modeId !== 'all' || period.auditDate !== currentDate || tool.config.kind !== 'salary-annual-rules-v1' || period.periodId !== String(tool.config.calendarYear) || period.policyRevisionId !== tool.configVersion) throw new Error('Maaş dönemi ve yapılandırma uyuşmuyor.');
  } else assertToolEffectiveForDate(tool, currentDate);
  return calculateSalary(
    { mode: canonicalSalaryMode(input.mode), monthlyAmount: input.monthlyAmount },
    salaryConfigFor(tool),
  );
}

export function calculateLegacyWebHgs(
  tool: ToolDefinition,
  input: {
    readonly crossing: LegacyHgsCrossing;
    readonly vehicleClass: 1 | 2 | 3 | 4 | 5 | 6;
    readonly time: HgsTime;
    readonly direction: HgsDirection;
    readonly currentDate: string;
  },
) {
  const crossing = canonicalHgsCrossing(input.crossing);
  assertToolEffectiveForDate(tool, input.currentDate);
  assertHgsCrossingEffective(tool, crossing, input.currentDate);
  return calculateHgsToll(
    {
      crossing,
      vehicleClass: `class-${input.vehicleClass}`,
      time: input.time,
      direction: input.direction,
    },
    hgsConfigFor(tool),
  );
}

function assertMathTool(tool: ToolDefinition, currentDate: string): void {
  assertToolEffectiveForDate(tool, currentDate);
}

export function calculateLegacyWebGeometry(
  tool: ToolDefinition,
  input: { readonly shape: LegacyGeometryShape; readonly dimensions: GeometryInput['dimensions'] },
  currentDate: string,
) {
  assertMathTool(tool, currentDate);
  return calculateGeometry(
    { shape: canonicalGeometryShape(input.shape), dimensions: input.dimensions },
    geometryConfigFor(tool),
  );
}

export function calculateLegacyWebPrime(
  tool: ToolDefinition,
  input: PrimeInput,
  currentDate: string,
) {
  assertMathTool(tool, currentDate);
  return calculatePrime(input, primeNumberConfigFor(tool));
}

export function calculateLegacyWebGcdLcm(
  tool: ToolDefinition,
  input: GcdLcmInput,
  currentDate: string,
) {
  assertMathTool(tool, currentDate);
  return calculateGcdLcm(input, gcdLcmConfigFor(tool));
}

export function calculateLegacyWebPowerRoot(
  tool: ToolDefinition,
  input: { readonly mode: LegacyPowerRootMode; readonly x: number; readonly n?: number },
  currentDate: string,
) {
  assertMathTool(tool, currentDate);
  const canonicalInput: PowerRootInput = input.n === undefined
    ? { mode: canonicalPowerRootMode(input.mode), x: input.x }
    : { mode: canonicalPowerRootMode(input.mode), x: input.x, n: input.n };
  return calculatePowerRoot(canonicalInput, powerRootConfigFor(tool));
}

export function calculateLegacyWebQuadratic(
  tool: ToolDefinition,
  input: QuadraticInput,
  currentDate: string,
) {
  assertMathTool(tool, currentDate);
  return calculateQuadratic(input, quadraticEquationConfigFor(tool));
}

export function calculateLegacyWebFraction(
  tool: ToolDefinition,
  input: Omit<FractionInput, 'operation'> & { readonly operation: LegacyFractionOperation },
  currentDate: string,
) {
  assertMathTool(tool, currentDate);
  return calculateFraction(
    { ...input, operation: canonicalFractionOperation(input.operation) },
    fractionConfigFor(tool),
  );
}

export function calculateLegacyWebRatio(
  tool: ToolDefinition,
  input: Omit<RatioInput, 'mode'> & { readonly mode: LegacyRatioMode },
  currentDate: string,
) {
  assertMathTool(tool, currentDate);
  return calculateRatio(
    { ...input, mode: canonicalRatioMode(input.mode) },
    ratioProportionConfigFor(tool),
  );
}

export function calculateLegacyWebPythagorean(
  tool: ToolDefinition,
  input: Omit<PythagoreanInput, 'mode'> & { readonly mode: LegacyPythagoreanMode },
  currentDate: string,
) {
  assertMathTool(tool, currentDate);
  return calculatePythagorean(
    { ...input, mode: canonicalPythagoreanMode(input.mode) },
    pythagoreanConfigFor(tool),
  );
}

/**
 * Ev/tadilat formu iç discriminantı kullanıcıdan kabul etmez. Registry kaydı
 * tarihi ve kapalı motor sözleşmesi doğrulandıktan sonra toolId adapter
 * tarafından en son eklenir; web de mobil ile aynı çekirdeği çalıştırır.
 */
export function calculateWebHomeRenovation(
  tool: ToolDefinition,
  userInput: Readonly<Record<string, unknown>>,
  currentDate: string,
) {
  assertToolEffectiveForDate(tool, currentDate);
  return calculateHomeRenovation(
    homeRenovationInputFor(tool, userInput),
    homeRenovationConfigFor(tool),
  );
}

/**
 * Tarih formu ham kullanıcı kaydını doğrudan çekirdeğe taşımaz; frozen
 * descriptor alanları runtime adapter tarafından seçilir ve receipt aynı
 * ToolDefinition provenance'ından üretilir.
 */
export function calculateWebDateTime(
  tool: ToolDefinition,
  userInput: Readonly<Record<string, unknown>>,
) {
  return calculateDateTimeTool(tool, userInput);
}

/**
 * Eğitim hesabında imzalı ToolDefinition tek otoritedir. Web katmanı kurum
 * profili overlay'i üretmez; yalnız açık İstanbul gününü aktiflik kapısına verir.
 */
export function calculateWebEducation(
  tool: ToolDefinition,
  userInput: Readonly<Record<string, unknown>>,
  currentDate: string,
) {
  return calculateEducationTool(tool, userInput, { currentDate });
}

const everydayHealthToolIds: ReadonlySet<string> = new Set<EverydayHealthToolId>([
  'lpg-payback',
  'fuel-consumption',
  'profit-margin',
  'basal-metabolic-rate',
  'historical-ideal-weight-reference',
]);

export interface WebEverydayHealthReceipt {
  readonly runtimeToolId: EverydayHealthToolId;
  readonly calculatorId: string;
  readonly engineId: string;
  readonly configVersion: string;
  readonly effectiveFrom: string;
  readonly lastReviewed: string;
  readonly reviewAfter: string | null;
  readonly updateMode: string;
  readonly sourceIds: readonly string[];
  readonly contentSourceIds: readonly string[];
  readonly limitations: readonly string[];
  readonly sourcePanelTitle: string | null;
  readonly sourcePanelSummary: string | null;
  readonly resultMetadata: {
    readonly calculatorId: string;
    readonly version: string;
    readonly effectiveFrom: string;
    readonly lastVerifiedAt: string;
    readonly sourceIds: readonly string[];
  };
}

function isEverydayHealthToolId(value: string): value is EverydayHealthToolId {
  return everydayHealthToolIds.has(value);
}

function assertExactUserFields(
  userInput: Readonly<Record<string, unknown>>,
  required: readonly string[],
  optional: readonly string[] = [],
): void {
  const allowed = new Set([...required, ...optional]);
  const unexpected = Object.keys(userInput).find((key) => !allowed.has(key));
  if (unexpected) {
    throw new Error(`Günlük/sağlık formu beklenmeyen alan içeriyor: ${unexpected}.`);
  }
}

function configWithoutKind<T extends { readonly kind: string }>(config: T): Omit<T, 'kind'> {
  const { kind: _kind, ...coreConfig } = config;
  return coreConfig;
}

function everydayHealthReceipt(
  tool: ToolDefinition,
  runtimeToolId: EverydayHealthToolId,
  result: { readonly metadata: {
    readonly calculatorId: string;
    readonly version: string;
    readonly effectiveFrom: string;
    readonly lastVerifiedAt: string;
    readonly sources: readonly { readonly id: string }[];
  } },
): WebEverydayHealthReceipt {
  const sourcePanel = tool.content.sourcePanel;
  return {
    runtimeToolId,
    calculatorId: result.metadata.calculatorId,
    engineId: tool.engineId,
    configVersion: tool.configVersion,
    effectiveFrom: tool.effectiveFrom,
    lastReviewed: tool.lastReviewed,
    reviewAfter: tool.updateCadence.reviewAfter,
    updateMode: tool.updateCadence.mode,
    sourceIds: tool.sources.map((source) => source.sourceId),
    contentSourceIds: [...(sourcePanel?.sourceIds ?? [])],
    limitations: [...tool.content.limitations],
    sourcePanelTitle: sourcePanel?.title ?? null,
    sourcePanelSummary: sourcePanel?.summary ?? null,
    resultMetadata: {
      calculatorId: result.metadata.calculatorId,
      version: result.metadata.version,
      effectiveFrom: result.metadata.effectiveFrom,
      lastVerifiedAt: result.metadata.lastVerifiedAt,
      sourceIds: result.metadata.sources.map((source) => source.id),
    },
  };
}

/**
 * Beş günlük/sağlık aracı yalnız doğrulanmış ToolDefinition üzerinden çalışır.
 * İç araç kimliği kullanıcı girdisinden alınmaz; config `kind` discriminantı
 * kontrol edildikten sonra çekirdeğin kapalı config tipine dönüştürülür.
 */
export function calculateWebEverydayHealth(
  tool: ToolDefinition,
  userInput: Readonly<Record<string, unknown>>,
  currentDate: string,
) {
  assertToolEffectiveForDate(tool, currentDate);
  if (!isEverydayHealthToolId(tool.toolId)) {
    throw new Error(`${tool.toolId} günlük/sağlık web adapterı tarafından desteklenmiyor.`);
  }

  let result;
  switch (tool.toolId) {
    case 'lpg-payback': {
      if (tool.engineId !== 'lpg-payback-v1' || tool.config.kind !== 'lpg-payback-v1') {
        throw new Error('LPG motor/config sözleşmesi geçersiz.');
      }
      assertExactUserFields(userInput, [
        'conversionCost', 'gasolineConsumptionL100Km', 'lpgConsumptionL100Km',
        'gasolinePricePerLitre', 'lpgPricePerLitre', 'monthlyDistanceKm',
      ], ['extraOneOffCost']);
      result = calculateCheckedLpgPayback(
        userInput,
        configWithoutKind(tool.config),
      );
      break;
    }
    case 'fuel-consumption': {
      if (tool.engineId !== 'fuel-consumption-v1' || tool.config.kind !== 'fuel-consumption-v1') {
        throw new Error('Yakıt motor/config sözleşmesi geçersiz.');
      }
      assertExactUserFields(userInput, [
        'fuelType', 'distanceKm', 'consumptionL100Km', 'pricePerLitre', 'direction',
      ]);
      result = calculateCheckedFuelConsumption(
        userInput,
        configWithoutKind(tool.config),
      );
      break;
    }
    case 'profit-margin': {
      if (tool.engineId !== 'profit-margin-v2' || tool.config.kind !== 'profit-margin-v2') {
        throw new Error('Kâr marjı motor/config sözleşmesi geçersiz.');
      }
      // Görünmeyen bir mod alanı DOM tahrifatıyla çekirdeğe taşınamaz. Her mod
      // yalnız kullanıcıya o anda gösterilen alan kümesini kabul eder.
      const mode = userInput.mode;
      switch (mode) {
        case 'targetSaleMargin':
          assertExactUserFields(userInput, ['mode', 'cost', 'targetMarginPercent']);
          break;
        case 'costMarkup':
          assertExactUserFields(userInput, ['mode', 'cost', 'markupPercent']);
          break;
        case 'analyzeSale':
          assertExactUserFields(userInput, ['mode', 'cost', 'salePrice']);
          break;
        default:
          assertExactUserFields(userInput, ['mode', 'cost']);
      }
      result = calculateCheckedProfitMargin(
        userInput,
        configWithoutKind(tool.config),
        mode,
      );
      break;
    }
    case 'basal-metabolic-rate': {
      if (tool.engineId !== 'basal-metabolic-rate-v1' || tool.config.kind !== 'basal-metabolic-rate-v1') {
        throw new Error('BMR motor/config sözleşmesi geçersiz.');
      }
      assertExactUserFields(userInput, ['sex', 'formula', 'ageYears', 'heightCm', 'weightKg']);
      // Formula değeri String() ile gevşetilmez; çekirdeğin iki literal
      // seçeneği dışındaki DOM tahrifatı UNSUPPORTED_VALUE olarak kapanır.
      result = calculateCheckedBmr(
        userInput,
        configWithoutKind(tool.config),
      );
      break;
    }
    case 'historical-ideal-weight-reference': {
      if (tool.engineId !== 'historical-ideal-weight-reference-v1'
          || tool.config.kind !== 'historical-ideal-weight-reference-v1') {
        throw new Error('İdeal kilo motor/config sözleşmesi geçersiz.');
      }
      assertExactUserFields(userInput, ['sex', 'heightCm'], ['currentWeightKg']);
      result = calculateCheckedIdealWeight(
        userInput,
        configWithoutKind(tool.config),
      );
      break;
    }
    default: {
      const exhaustive: never = tool.toolId;
      throw new Error(`Günlük/sağlık adapter sözleşmesi eksik: ${exhaustive}.`);
    }
  }

  return {
    result,
    receipt: everydayHealthReceipt(tool, tool.toolId, result),
  };
}

export {
  parseBrowserToolDefinitions,
  bootstrapSignedRegistry,
  fetchSignedRegistrySnapshot,
} from './signedRegistry.js';
export type {
  BrowserRegistryStatus,
  SignedRegistryBootstrapConfig,
  SignedRegistryFetchOptions,
  VerifiedWebRegistrySnapshot,
} from './signedRegistry.js';
